import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AD_PROVIDER_LABELS, accountLabel, fromMicros, percentOf, toMicros } from "@app/shared";
import { ChipGroup } from "@/components/app/ChipGroup";
import { TextareaField, TextField, useNotify } from "@/components/app/page";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { api, type CabinetBalance } from "@/lib/api";
import { money, pct } from "@/lib/format";
import { cn } from "@/lib/utils";

const DECIMAL = /^\d+(\.\d{1,2})?$/;
const RATE = /^-?\d+(\.\d{1,2})?$/;
const HUNDRED = toMicros("100");

/** What the account gets back for `amount` left on a cabinet funded at `rate`. */
const returnedForRemainder = (amount: bigint, rate: string) => (amount * HUNDRED) / (HUNDRED - toMicros(rate));
const usd = (micros: bigint) => money(fromMicros(micros, 2));
const signed = (micros: bigint) => `${micros > 0n ? "+" : ""}${usd(micros)}`;

/** Everything a money dialog invalidates: it moved between two balances. */
export const MONEY_KEYS = [
  "clients",
  "client",
  "balances",
  "cabinets",
  "topups",
  "allTopUps",
  "stats",
  "pnl",
  "forecast",
];

function Line({
  label,
  value,
  strong,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-3",
        muted && "text-muted-foreground",
        strong && "font-medium",
      )}
    >
      <span className="shrink-0">{label}</span>
      <span className="min-w-0 truncate text-right tabular-nums">{value}</span>
    </div>
  );
}

function CabinetLine({ cabinet }: { cabinet: CabinetBalance }) {
  return (
    <div className="bg-secondary/60 grid gap-1.5 rounded-2xl p-3 text-sm">
      <Line label="Кабинет" value={cabinet.title} strong />
      <Line label="Платформа" value={AD_PROVIDER_LABELS[cabinet.provider]} muted />
      <Line label="ID" value={<span className="font-mono text-xs">{cabinet.externalId}</span>} muted />
      <Line label="Остаток кабинета" value={money(cabinet.availableBalance)} muted />
    </div>
  );
}

/**
 * Funds one cabinet from the client's account. This is where commission is
 * charged for the platforms that keep money on the cabinet: the account pays
 * the whole amount, and the cabinet gets what is left after it.
 */
export function AllocateDialog({
  clientId,
  cabinet,
  onOpenChange,
}: {
  clientId: number;
  cabinet: CabinetBalance | null;
  onOpenChange: (open: boolean) => void;
}) {
  const open = cabinet !== null;
  const qc = useQueryClient();
  const notify = useNotify();

  const client = useQuery({ queryKey: ["client", clientId], queryFn: () => api.client(clientId), enabled: open });
  const balances = useQuery({ queryKey: ["balances", clientId], queryFn: () => api.balances(clientId), enabled: open });

  const [amount, setAmount] = useState("");
  const [withCommission, setWithCommission] = useState(true);
  const [rate, setRate] = useState("0");
  const [note, setNote] = useState("");

  const ladderRate = client.data?.ratePercent;
  useEffect(() => {
    if (!open) return;
    setAmount("");
    setNote("");
    setWithCommission(true);
  }, [open]);
  useEffect(() => {
    if (ladderRate !== undefined) setRate(ladderRate);
  }, [ladderRate, open]);

  // The client's account on this cabinet's platform: money paid in for it
  // that is not on any of its cabinets yet.
  const accountBalance = toMicros(balances.data?.find((b) => b.provider === cabinet?.provider)?.accountBalance ?? "0");
  const amountMicros = DECIMAL.test(amount.trim()) ? toMicros(amount.trim()) : null;
  const rateValid = !withCommission || (RATE.test(rate.trim()) && Math.abs(Number(rate.trim())) <= 100);
  const effectiveRate = withCommission && rateValid ? rate.trim() : "0";

  const preview = useMemo(() => {
    if (amountMicros === null || amountMicros <= 0n || !rateValid) return null;
    const fee = percentOf(amountMicros, effectiveRate);
    return { fee, net: amountMicros - fee };
  }, [amountMicros, rateValid, effectiveRate]);

  const submit = useMutation({
    mutationFn: () =>
      api.allocate(clientId, {
        adAccountId: cabinet!.adAccountId,
        amount: amount.trim(),
        ratePercent: effectiveRate,
        note: note.trim() || undefined,
      }),
    onSuccess: (r) => {
      for (const key of MONEY_KEYS) void qc.invalidateQueries({ queryKey: [key] });
      notify(
        `Кабинет пополнен · ${cabinet?.title}`,
        `Со счёта ${money(r.cabinet.grossAmount)} · комиссия ${pct(r.cabinet.ratePercent)} → на кабинет ${money(r.cabinet.netAmount)}`,
        "green",
      );
      onOpenChange(false);
    },
    onError: () => notify("Не удалось пополнить кабинет", undefined, "destructive"),
  });

  const after = preview ? accountBalance - amountMicros! : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel="Закрыть">
        <DialogHeader>
          <DialogTitle>Пополнить кабинет со счёта</DialogTitle>
          <DialogDescription>
            Со счёта клиента в этой платформе списывается вся сумма, комиссия остаётся нам, остальное идёт на баланс
            кабинета.
          </DialogDescription>
        </DialogHeader>

        {cabinet && (
          <DialogBody className="grid gap-5">
            <CabinetLine cabinet={cabinet} />

            <TextField
              label={`Сумма со счёта ${AD_PROVIDER_LABELS[cabinet.provider]}, $`}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(",", "."))}
              className="tabular-nums"
              placeholder="1000"
              description={`${accountLabel(cabinet.provider)}: ${money(fromMicros(accountBalance, 2))}`}
              error={amount && amountMicros === null ? "Сумма в долларах, до 2 знаков после точки" : undefined}
            />

            <div className="flex min-w-0 flex-col gap-2">
              <span className="text-sm leading-none font-medium">Комиссия</span>
              <ChipGroup
                label="Комиссия"
                fullWidth
                options={[
                  { value: "on", label: "С комиссией" },
                  { value: "off", label: "Без комиссии" },
                ]}
                value={withCommission ? "on" : "off"}
                onValueChange={(v) => setWithCommission(v === "on")}
              />
              {withCommission && (
                <TextField
                  label="Ставка"
                  inputMode="decimal"
                  value={rate}
                  onChange={(e) => setRate(e.target.value.replace(",", "."))}
                  className="tabular-nums"
                  icon={<span className="text-muted-foreground text-sm">%</span>}
                  iconPosition="end"
                  error={rateValid ? undefined : "Ставка от −100 до 100"}
                  description={`По лесенке сейчас ${ladderRate ? pct(ladderRate) : "—"}. Эту же ставку вернём, если остаток пойдёт обратно на счёт.`}
                />
              )}
            </div>

            <TextareaField
              label="Описание"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="необязательно"
            />

            {preview && (
              <div className="bg-secondary/50 border-border grid gap-1 rounded-xl border p-3 text-sm">
                <Line label="Спишется со счёта" value={`−${usd(amountMicros!)}`} />
                {preview.fee !== 0n && (
                  <Line label={`Комиссия ${pct(effectiveRate)}`} value={`−${usd(preview.fee)}`} muted />
                )}
                <Line label="Зачислится на кабинет" value={`+${usd(preview.net)}`} strong />
                <div className="border-border mt-1 grid gap-1 border-t pt-2">
                  <Line label={accountLabel(cabinet.provider)} value={`${usd(accountBalance)} → ${usd(after!)}`} />
                  <Line
                    label="Баланс кабинета"
                    value={`${money(cabinet.availableBalance)} → ${usd(toMicros(cabinet.availableBalance) + preview.net)}`}
                  />
                </div>
              </div>
            )}

            {after !== null && after < 0n && (
              <Alert size="sm" color="destructive">
                На счету столько нет — он уйдёт в минус на {usd(-after)}.
              </Alert>
            )}

            <Alert size="sm" color="neutral">
              Клиенту в Telegram уйдёт уведомление о пополнении кабинета.
            </Alert>
          </DialogBody>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={() => submit.mutate()} disabled={!preview || submit.isPending}>
            {submit.isPending && <Spinner />}
            Пополнить кабинет
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Returns what is left on a cabinet to the client's account -- what we do when
 * a cabinet is banned or switched off for good. The commission charged when it
 * was funded goes back with the money, at that same rate unless it is changed.
 */
export function ReturnDialog({
  cabinet,
  onOpenChange,
}: {
  cabinet: CabinetBalance | null;
  onOpenChange: (open: boolean) => void;
}) {
  const open = cabinet !== null;
  const qc = useQueryClient();
  const notify = useNotify();

  const [amount, setAmount] = useState("");
  const [withCommission, setWithCommission] = useState(true);
  const [rate, setRate] = useState("0");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!cabinet) return;
    // Everything the cabinet has left, at the rate its last funding was
    // charged -- the two defaults that make this one click for a dead cabinet.
    setAmount(toMicros(cabinet.availableBalance) > 0n ? cabinet.availableBalance : "");
    setRate(cabinet.lastRatePercent ?? "0");
    setWithCommission(cabinet.lastRatePercent !== null && toMicros(cabinet.lastRatePercent) !== 0n);
    setNote("");
  }, [cabinet]);

  const balance = toMicros(cabinet?.availableBalance ?? "0");
  const amountMicros = DECIMAL.test(amount.trim()) ? toMicros(amount.trim()) : null;
  const rateValid = !withCommission || (RATE.test(rate.trim()) && Math.abs(Number(rate.trim())) < 100);
  const effectiveRate = withCommission && rateValid ? rate.trim() : "0";

  const preview = useMemo(() => {
    if (amountMicros === null || amountMicros <= 0n || !rateValid) return null;
    // The reverse of the funding, so a round trip with no spend comes back
    // whole: 3,000 at 8% returns 3,000 / 0.92, not 3,000 + 8%.
    const credit = toMicros(fromMicros(returnedForRemainder(amountMicros, effectiveRate), 2));
    return { fee: credit - amountMicros, credit };
  }, [amountMicros, rateValid, effectiveRate]);

  const submit = useMutation({
    mutationFn: () =>
      api.returnToAccount(cabinet!.adAccountId, {
        amount: amount.trim(),
        ratePercent: effectiveRate,
        note: note.trim() || undefined,
      }),
    onSuccess: (r) => {
      for (const key of MONEY_KEYS) void qc.invalidateQueries({ queryKey: [key] });
      void qc.invalidateQueries({ queryKey: ["accounts"] });
      notify(
        `Остаток возвращён на счёт · ${cabinet?.title}`,
        `С кабинета ${money(String(-Number(r.cabinet.netAmount)))} → на счёт ${money(r.account.netAmount)}`,
        "green",
      );
      onOpenChange(false);
    },
    onError: (e: Error) =>
      notify(
        e.message === "NOTHING_TO_RETURN"
          ? "На кабинете нечего возвращать"
          : e.message === "RETURN_COMMISSION_EXCEEDS_CHARGED"
            ? "Комиссия больше, чем брали с этого кабинета"
            : "Не удалось вернуть остаток",
        e.message === "RETURN_COMMISSION_EXCEEDS_CHARGED"
          ? "Вернуть можно только ту комиссию, которая была удержана при пополнении. Снимите галочку или поставьте ставку ниже."
          : undefined,
        "destructive",
      ),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel="Закрыть">
        <DialogHeader>
          <DialogTitle>Вернуть остаток на счёт</DialogTitle>
          <DialogDescription>
            Остаток кабинета возвращается на счёт клиента в этой платформе вместе с комиссией, удержанной при его
            пополнении.
          </DialogDescription>
        </DialogHeader>

        {cabinet && (
          <DialogBody className="grid gap-5">
            <CabinetLine cabinet={cabinet} />

            {cabinet.disableReason && (
              <Alert size="sm" color="amber" title="Кабинет недоступен">
                {cabinet.disableReason}
              </Alert>
            )}

            <TextField
              label="Сумма с кабинета, $"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(",", "."))}
              className="tabular-nums"
              description="По умолчанию — весь остаток кабинета."
              error={amount && amountMicros === null ? "Сумма в долларах, до 2 знаков после точки" : undefined}
            />

            <div className="flex min-w-0 flex-col gap-2">
              <span className="text-sm leading-none font-medium">Комиссия</span>
              <ChipGroup
                label="Комиссия"
                fullWidth
                options={[
                  { value: "on", label: "Вернуть комиссию" },
                  { value: "off", label: "Без комиссии" },
                ]}
                value={withCommission ? "on" : "off"}
                onValueChange={(v) => setWithCommission(v === "on")}
              />
              {withCommission && (
                <TextField
                  label="Ставка"
                  inputMode="decimal"
                  value={rate}
                  onChange={(e) => setRate(e.target.value.replace(",", "."))}
                  className="tabular-nums"
                  icon={<span className="text-muted-foreground text-sm">%</span>}
                  iconPosition="end"
                  error={rateValid ? undefined : "Ставка больше −100 и меньше 100"}
                  description={
                    cabinet.lastRatePercent
                      ? `Последнее пополнение кабинета было по ${pct(cabinet.lastRatePercent)}. Вернём столько же, сколько по этой ставке пришлось списать со счёта.`
                      : "Кабинет ещё не пополняли со счёта."
                  }
                />
              )}
            </div>

            <TextareaField
              label="Описание"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="необязательно"
            />

            {preview && (
              <div className="bg-secondary/50 border-border grid gap-1 rounded-xl border p-3 text-sm">
                <Line label="Спишется с кабинета" value={`−${usd(amountMicros!)}`} />
                {preview.fee !== 0n && (
                  <Line label={`Возврат комиссии ${pct(effectiveRate)}`} value={signed(preview.fee)} muted />
                )}
                <Line label="Зачислится на счёт" value={`+${usd(preview.credit)}`} strong />
                <div className="border-border mt-1 border-t pt-2">
                  <Line label="Баланс кабинета" value={`${usd(balance)} → ${usd(balance - amountMicros!)}`} />
                </div>
              </div>
            )}

            {preview && amountMicros! > balance && (
              <Alert size="sm" color="amber">
                Это больше остатка кабинета — он уйдёт в минус на {usd(amountMicros! - balance)}.
              </Alert>
            )}
          </DialogBody>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={() => submit.mutate()} disabled={!preview || submit.isPending}>
            {submit.isPending && <Spinner />}
            Вернуть на счёт
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
